import { LoadingState, QueryError } from '#/components/common/loading-state'
/**
 * `/workspace/:workspaceId/settings` — Team & settings (Design Room plan F0).
 * Members / facilitators / clients on every tier (seats per tier), branding, and the workspace activity feed.
 */
import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AppHeader } from '#/components/layout/app-header'
import { BrandingCard } from '#/components/studio/branding-card'
import { MembersCard } from '#/components/studio/members-card'
import { RulesEditor } from '#/components/rules/rules-editor'
import { DecisionsEditor } from '#/components/decisions/decisions-editor'
import { StudioSection } from '#/components/studio/shared'
import { ActivityFeed } from '#/components/workspace/activity-feed'
import { FallbackSettingsCard } from '#/components/workspace/fallback-settings-card'
import { useWorkspacePlans } from '#/lib/api'
import { useMembers } from '#/lib/api-studio'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/workspace/$workspaceId/settings')({
  head: () => ({ meta: [{ title: m.tm_page_title() }] }),
  component: SettingsPage,
})

function SettingsPage() {
  const { workspaceId } = Route.useParams()
  const { data: ws } = useWorkspacePlans(workspaceId)
  const memberQuery = useMembers(workspaceId)
  const { data: members } = memberQuery
  const [seatsGated, setSeatsGated] = useState(false)

  if (memberQuery.isError) return <><AppHeader /><QueryError onRetry={() => memberQuery.refetch()} retrying={memberQuery.isFetching} /></>
  if (memberQuery.isLoading) return <><AppHeader /><LoadingState layout="document" /></>
  return (
    <>
      <AppHeader
        showStats={false}
        compact
        left={
          <nav className="hidden items-center gap-1 truncate text-sm text-muted-foreground lg:flex">
            <span>/</span>
            <Link to="/workspace/$workspaceId" params={{ workspaceId }} className="truncate hover:text-foreground">
              {ws?.workspace.name ?? '…'}
            </Link>
            <span>/</span>
            <span className="truncate text-foreground">{m.tm_page_title()}</span>
          </nav>
        }
      />
      <main className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div>
            <h1 className="font-heading text-2xl font-semibold">{m.tm_page_title()}</h1>
            <p className="mt-1 max-w-prose text-sm text-muted-foreground">{m.tm_page_desc()}</p>
          </div>
          {seatsGated ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-dashed bg-muted/40 p-4 text-sm">
              <span>{m.tm_err_seats({ max: members?.seats?.limit ?? 1 })}</span>
              <Link to="/pricing" search={{ voucher: undefined }} className="font-medium text-primary underline-offset-4 hover:underline">
                {m.tm_upgrade()}
              </Link>
            </div>
          ) : null}
          <MembersCard workspaceId={workspaceId} onGated={(code) => { if (code === 'seats_exhausted') setSeatsGated(true) }} />
          <p className="px-1 text-xs text-muted-foreground">{m.tm_notify_hint()}</p>
          <BrandingCard workspaceId={workspaceId} branding={members?.branding} fallbackName={ws?.workspace.name} />
          {/* Inspirasi F3 — plain-language always / never / ask rules every agent receives */}
          <RulesEditor workspaceId={workspaceId} />
          {/* Parity P3 — the decision journal (supersede chain) every agent receives after the rules */}
          <DecisionsEditor workspaceId={workspaceId} />
          {/* Orkestrator E4 §10 — what may be offered when the computer / AI provider has trouble (+ GitHub issue import, dev mode) */}
          <FallbackSettingsCard workspaceId={workspaceId} />
          <StudioSection title={m.tm_activity_title()} desc={m.tm_activity_desc()}>
            <ActivityFeed workspaceId={workspaceId} />
          </StudioSection>
        </div>
      </main>
    </>
  )
}
